import type {
  LocationId,
  NodeId,
  PresenceSpace,
  TravelHubNode,
  WorldDefinition,
} from '@/world-prototype/types';
import { pointInPolygon, pointInAnyObstacle } from '@/world-prototype/movement/walkArea';

export function getLocation(
  world: WorldDefinition,
  locationId: LocationId,
) {
  const location = world.locations[locationId];
  if (!location) {
    throw new Error(`Unknown location "${locationId}" in world "${world.id}"`);
  }
  return location;
}

export function getLocationDisplayName(
  world: WorldDefinition,
  locationId: string,
): string {
  return world.locations[locationId]?.displayName ?? locationId;
}

export function getPresenceSpace(
  world: WorldDefinition,
  locationId: LocationId,
): PresenceSpace | null {
  return world.spaces[locationId] ?? null;
}

export function getTravelHub(
  world: WorldDefinition,
  nodeId: NodeId,
): TravelHubNode | null {
  return world.hubs[nodeId] ?? null;
}

export function resolveTravelArrivalNodeId(
  world: WorldDefinition,
  locationId: LocationId,
): NodeId {
  const location = getLocation(world, locationId);
  return location.defaultViewId;
}

export function getBreadcrumbLabels(
  world: WorldDefinition,
  nodeId: NodeId,
): readonly string[] {
  const location =
    world.locations[nodeId] ??
    (world.hubs[nodeId]
      ? world.locations[world.hubs[nodeId]!.locationId]
      : undefined);
  const name = location?.displayName ?? nodeId;
  return [world.name, name];
}

export function isPresenceLocation(
  world: WorldDefinition,
  locationId: LocationId,
): boolean {
  return world.locations[locationId]?.kind === 'presence';
}

export function validateWorld(world: WorldDefinition): readonly string[] {
  const errors: string[] = [];
  const locationIds = new Set(Object.keys(world.locations));
  const spaceIds = new Set(Object.keys(world.spaces));
  const hubIds = new Set(Object.keys(world.hubs));

  if (!locationIds.has(world.startId) && !hubIds.has(world.startId)) {
    errors.push(`startId "${world.startId}" is not a location or hub`);
  }

  for (const [key, location] of Object.entries(world.locations)) {
    if (key !== location.id) {
      errors.push(`Location key "${key}" does not match id "${location.id}"`);
    }
    if (location.kind === 'presence') {
      if (!spaceIds.has(location.defaultViewId)) {
        errors.push(
          `Presence location "${location.id}" missing space "${location.defaultViewId}"`,
        );
      }
    } else if (!hubIds.has(location.defaultViewId)) {
      errors.push(
        `Travel-hub location "${location.id}" missing hub "${location.defaultViewId}"`,
      );
    }
  }

  for (const [key, space] of Object.entries(world.spaces)) {
    if (key !== space.id) {
      errors.push(`Space key "${key}" does not match id "${space.id}"`);
    }
    if (!locationIds.has(space.id)) {
      errors.push(`Space "${space.id}" has no matching location`);
    }
    const b = space.walkBounds;
    if (b.xMin >= b.xMax || b.yMin >= b.yMax) {
      errors.push(`Space "${space.id}" has invalid walkBounds`);
    }
    if (space.walkPolygon) {
      if (space.walkPolygon.length < 3) {
        errors.push(`Space "${space.id}" walkPolygon needs at least 3 points`);
      } else {
        if (!pointInPolygon(space.start.x, space.start.y, space.walkPolygon)) {
          errors.push(`Space "${space.id}" start is outside walkPolygon`);
        }
        for (const obj of space.interactiveObjects ?? []) {
          if (!pointInPolygon(obj.standX, obj.standY, space.walkPolygon)) {
            errors.push(
              `Space "${space.id}" object "${obj.id}" stand point outside walkPolygon`,
            );
          }
          if (
            pointInAnyObstacle(
              obj.standX,
              obj.standY,
              space.walkObstacles,
            )
          ) {
            errors.push(
              `Space "${space.id}" object "${obj.id}" stand point inside a walkObstacle`,
            );
          }
        }
      }
    }
    for (const obstacle of space.walkObstacles ?? []) {
      if (obstacle.polygon.length < 3) {
        errors.push(
          `Space "${space.id}" walkObstacle "${obstacle.id}" needs at least 3 points`,
        );
      }
    }
    if (
      pointInAnyObstacle(space.start.x, space.start.y, space.walkObstacles)
    ) {
      errors.push(`Space "${space.id}" start is inside a walkObstacle`);
    }
    for (const utility of space.utilityTravel) {
      if (!locationIds.has(utility.targetLocationId)) {
        errors.push(
          `Space "${space.id}" utility "${utility.id}" → unknown "${utility.targetLocationId}"`,
        );
      }
    }
    if (!space.scenery?.layers) {
      errors.push(`Space "${space.id}" is missing scenery.layers`);
    } else {
      const layerIds = new Set(space.scenery.layers.map((l) => l.id));
      for (const obj of space.interactiveObjects ?? []) {
        if (obj.sceneryId && !layerIds.has(obj.sceneryId)) {
          errors.push(
            `Space "${space.id}" object "${obj.id}" sceneryId "${obj.sceneryId}" missing`,
          );
        }
      }
    }
  }

  for (const [key, hub] of Object.entries(world.hubs)) {
    if (key !== hub.id) {
      errors.push(`Hub key "${key}" does not match id "${hub.id}"`);
    }
    if (!locationIds.has(hub.locationId)) {
      errors.push(`Hub "${hub.id}" location "${hub.locationId}" is missing`);
    }
    for (const utility of hub.utilityTravel) {
      if (!locationIds.has(utility.targetLocationId)) {
        errors.push(
          `Hub "${hub.id}" utility "${utility.id}" → unknown "${utility.targetLocationId}"`,
        );
      }
    }
  }

  return errors;
}
